import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiHeart, FiRefreshCw, FiTruck } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/main.scss";

function Main() {
  const { toggleWishlist, isInWishlist } = useContext(CartContext);
  const usedHomeImages = new Set();
  const takeOne = (predicate) => {
    const product = productData.find((item) => predicate(item) && !usedHomeImages.has(item.image))
      || productData.find((item) => !usedHomeImages.has(item.image))
      || productData[0];
    usedHomeImages.add(product.image);
    return product;
  };
  const takeMany = (predicate, count) => {
    const products = productData.filter((item) => predicate(item) && !usedHomeImages.has(item.image)).slice(0, count);
    products.forEach((product) => usedHomeImages.add(product.image));
    return products;
  };

  const womenHero = takeOne((product) => product.gender === "Women");
  const menHero = takeOne((product) => product.gender === "Men");
  const newArrivals = takeMany((product) => product.tags.includes("new-in"), 4);
  const editorialProduct = takeOne((product) => product.tags.includes("sale"));
  const manifestoWomen = takeOne((product) => product.category === "Jumpsuits");
  const manifestoMen = takeOne((product) => product.gender === "Men" && product.category === "Jackets");
  const favourites = takeMany((product) => product.tags.includes("featured"), 4);
  const preorderProduct = takeOne((product) => product.tags.includes("pre-order"));

  const renderProductCard = (product) => (
    <article className="home-product" key={product.id}>
      <div className="home-product__media">
        <Link to={`/shopDetails/${product.id}`} aria-label={`${product.name}, ${product.colorName}`}>
          <img src={product.image} alt={`${product.name} in ${product.colorName}`} loading="lazy" />
        </Link>
        <div className="home-product__badges">
          {product.badges.map((badge) => <span key={badge}>{badge}</span>)}
        </div>
        <button
          type="button"
          className={isInWishlist(product.id) ? "is-active" : ""}
          onClick={() => toggleWishlist(product.id)}
          aria-label={`${isInWishlist(product.id) ? "Remove" : "Add"} ${product.name} ${isInWishlist(product.id) ? "from" : "to"} wishlist`}
        >
          <FiHeart />
        </button>
      </div>
      <Link className="home-product__info" to={`/shopDetails/${product.id}`}>
        <span>
          <strong>{product.name}</strong>
          <small>{product.colorName}</small>
        </span>
        <span className="home-product__price">
          {formatPrice(product.price)}
          {product.compareAt && <del>{formatPrice(product.compareAt)}</del>}
        </span>
      </Link>
      <div className="home-product__sizes" aria-label="Available sizes">
        {product.sizes.map((size) => <span key={size}>{size}</span>)}
      </div>
    </article>
  );

  return (
    <main className="home-page">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <Link className="home-hero__image home-hero__image--women" to="/shop?collection=women" aria-label="Shop women">
          <img src={womenHero.image} alt={`${womenHero.name} for women, front product view`} />
          <span>Women</span>
        </Link>
        <Link className="home-hero__image home-hero__image--men" to="/shop?collection=men" aria-label="Shop men">
          <img src={menHero.image} alt={`${menHero.name} for men, front product view`} />
          <span>Men</span>
        </Link>
        <div className="home-hero__content">
          <p className="eyebrow">{siteConfig.commerce.seasonLabel}</p>
          <h1 id="home-hero-title">Wear it.<br />Repeat it.</h1>
          <p>Tactile layers, breathable tailoring and quiet colour for everyday use.</p>
          <div>
            <Link className="button-light" to="/shop?collection=women">Shop women</Link>
            <Link className="button-light" to="/shop?collection=men">Shop men</Link>
          </div>
        </div>
      </section>

      <section className="home-assurances" aria-label="Store assurances">
        <div><span>01</span><strong>Materials stated</strong><small>Composition and care on every product.</small></div>
        <div><span>02</span><strong>Useful construction</strong><small>Pockets, fit and finish shown in detail.</small></div>
        <div><span>03</span><strong>Straightforward checkout</strong><small>{siteConfig.business.currency} totals and {siteConfig.commerce.paymentPlatform} payment.</small></div>
      </section>

      <section className="home-products page-shell">
        <div className="section-heading-row">
          <div>
            <p className="eyebrow">Just landed</p>
            <h2>New for the rotation</h2>
          </div>
          <Link className="text-link" to="/shop?status=new-in">View all <FiArrowRight /></Link>
        </div>
        <div className="home-product-grid">{newArrivals.map(renderProductCard)}</div>
      </section>

      <section className="home-editorial">
        <div className="home-editorial__image">
          <img src={editorialProduct.image} alt={`${editorialProduct.name} in ${editorialProduct.colorName}`} loading="lazy" />
        </div>
        <div className="home-editorial__copy">
          <p className="eyebrow">Colour study</p>
          <h2>Quiet shades, clear purpose.</h2>
          <p>Forest, salt, clay and charcoal are selected to work across shirts, knits and trousers already in a wardrobe.</p>
          <Link className="text-link" to="/shop?collection=women">Explore the edit <FiArrowRight /></Link>
        </div>
      </section>

      <section className="home-manifesto page-shell">
        <div className="home-manifesto__collage">
          <img src={manifestoWomen.image} alt={`${manifestoWomen.name} product views`} loading="lazy" />
          <img src={manifestoMen.image} alt={`${manifestoMen.name} product views`} loading="lazy" />
        </div>
        <div className="home-manifesto__copy">
          <p className="eyebrow">Our starting point</p>
          <h2>A useful wardrobe begins with honest product details.</h2>
          <p>{siteConfig.description} We show material, fit, available sizes, care and cautions so each choice can be made with context.</p>
          <Link className="button-secondary" to="/about">Read our story</Link>
        </div>
      </section>

      <section className="home-quote">
        <p className="eyebrow">The repeat test</p>
        <blockquote>“The best piece is the one<br />you reach for again.”</blockquote>
      </section>

      <section className="home-products page-shell">
        <div className="section-heading-row">
          <div>
            <p className="eyebrow">Community favourites</p>
            <h2>Worn on repeat</h2>
          </div>
          <Link className="text-link" to="/shop">Shop all <FiArrowRight /></Link>
        </div>
        <div className="home-product-grid">{favourites.map(renderProductCard)}</div>
      </section>

      <section className="home-preorder">
        <div className="home-preorder__image">
          <img src={preorderProduct.image} alt={`${preorderProduct.name} in ${preorderProduct.colorName}`} loading="lazy" />
        </div>
        <div className="home-preorder__copy">
          <p className="eyebrow">Pre-order, explained</p>
          <h2>Not ready for immediate dispatch.</h2>
          <p>Pre-order products are marked throughout the store. No fixed dispatch date is configured, so ask customer care for the current estimate if timing matters.</p>
          <Link className="button-light" to="/shop?status=pre-order">Shop pre-order</Link>
        </div>
      </section>

      <section className="home-service page-shell" aria-label="Shopping benefits">
        <div><FiTruck /><strong>Fixed shipping · {formatPrice(siteConfig.commerce.standardShipping)}</strong><span>Order-specific updates are sent by email.</span></div>
        <div><FiRefreshCw /><strong>Clear return process</strong><span>Contact us promptly; condition and mandatory-rights rules apply.</span></div>
        <div><span className="home-service__mark">{siteConfig.logo.symbol}</span><strong>Customer care by email</strong><span>{siteConfig.contact.email}</span></div>
      </section>
    </main>
  );
}

export default Main;
